import React, { useState, useEffect } from 'react';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { RadioButton } from 'primereact/radiobutton';
import { Panel } from 'primereact/panel';
import { Divider } from 'primereact/divider';
import { Image } from 'primereact/image';
import { Tooltip } from 'primereact/tooltip';
import axios from 'axios';
import i18n from '../../../i18n';

const styles = {
  fieldContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
    marginBottom: '1rem',
  },
  label: {
    fontWeight: '600',
    fontSize: '0.95rem',
  },
  errorText: {
    color: '#e24c4b',
    fontSize: '0.8rem',
    marginTop: '0.2rem',
  },
  groupHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  icon: {
    width: 20,
    height: 20,
  },
  submitButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#007ad9',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '1rem',
    marginTop: '2rem',
  },
};

const OeDynamicForm = ({ manufacturer_service_book_entry, workshop, vehicle, onChange }) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [latestFormData, setLatestFormData] = useState(null);

  useEffect(() => {
    const fetchLatestFormData = async () => {
      if (!manufacturer_service_book_entry || !manufacturer_service_book_entry.id) return;
      if (!workshop || !workshop.id) return;

      try {
        const response = await axios.get(`/uiapi/1/service-book-entries/${manufacturer_service_book_entry.id}/?workshop_id=${workshop.id}`);
        setLatestFormData(response.data.form_data);
      } catch (err) {
        console.error('Failed to fetch latest form data:', err);
      }
    };

    fetchLatestFormData();
  }, [manufacturer_service_book_entry && manufacturer_service_book_entry.id, workshop && workshop.id]);

  useEffect(() => {
    const fetchSchema = async () => {
      try {
        const response = await axios.get(`/api/1/integrations/oe/maintenance-schema/?workshop_id=${workshop.id}&vehicle_id=${vehicle.id}`);
        setSchema(response.data);
      } catch (err) {
        setError(err.response && err.response.data && err.response.data.error || 'Failed to fetch schema');
      } finally {
        setLoading(false);
      }
    };

    if (workshop && workshop.id && vehicle && vehicle.id) {
      fetchSchema();
    }
  }, [workshop, vehicle]);

  useEffect(() => {
    if (!schema) return;

    const initialData = {};
    const populateDefaults = (fields) => {
      fields.forEach((field) => {
        // Check if this field has conditional validation
        if (field.validations && field.validations.when) {
          const depField = field.validations.when.deps[0];
          const expectedValue = field.validations.when.params.is;

          // Only set default if parent condition is met
          if (formData[depField] === expectedValue) {
            const defaultValue = getDefaultValue(field);
            initialData[field.name] = defaultValue;
          }
        } else {
          // For non-conditional fields, always set default
          const defaultValue = getDefaultValue(field);
          initialData[field.name] = defaultValue;
        }

        if (field.children && field.children.length) {
          populateDefaults(field.children);
        }
      });
    };

    const sections = (schema && schema.sections) || [];
    sections.forEach((section) => {
      (section.groups || []).forEach((group) => {
        populateDefaults(group.fields || []);
      });
    });

    // Load initial data from latest form data if available, otherwise fall back to manufacturer_service_book_entry data
    const savedFormData = latestFormData ||
      (manufacturer_service_book_entry && manufacturer_service_book_entry.form_data
        ? manufacturer_service_book_entry.form_data
        : {});
    const mergedData = { ...initialData, ...savedFormData };

    setFormData(mergedData);
    if (onChange) onChange(mergedData);
  }, [schema, latestFormData, manufacturer_service_book_entry && manufacturer_service_book_entry.form_data]);

  const updateValue = (name, value) => {
    let updated = { ...formData };
    const field = findFieldByName(schema, name);

    // Handle conditional children when parent value changes
    if (field && field.children && field.children.length > 0) {
      field.children.forEach(child => {
        if (child.validations && child.validations.when) {
          const depField = child.validations.when.deps[0];
          const expectedValue = child.validations.when.params.is;

          // If parent value doesn't match condition, remove child from form data
          if (value !== expectedValue) {
            delete updated[child.name];
          }
        }
      });
    }

    // Update the value
    updated[name] = value;
    setFormData(updated);
    if (onChange) onChange(updated);
    validateField(name, value, updated);

    // Save to backend
    if (manufacturer_service_book_entry && manufacturer_service_book_entry.id) {
      axios.patch(`/uiapi/1/service-book-entries/${manufacturer_service_book_entry.id}/?workshop_id=${workshop.id}`, {
        form_data: updated,
      }).catch(err => {
        console.error('Failed to save form data:', err);
      });
    }
  };

  const validateField = (name, value, allValues) => {
    const field = findFieldByName(schema, name);
    if (!field) return;

    const fieldErrors = [];

    // Handle conditional validation
    if (field.validations && field.validations.when) {
      const depField = field.validations.when.deps[0];
      const expectedValue = field.validations.when.params.is;
      const notExpectedValue = field.validations.when.params.isnt;
      const isValueEmpty = value === '' || value === null || value === undefined;

      // Check if condition is met
      let conditionMet = false;
      if (expectedValue !== undefined) {
        conditionMet = allValues[depField] === expectedValue;
      } else if (notExpectedValue !== undefined) {
        conditionMet = allValues[depField] !== notExpectedValue;
      }

      // If parent condition is met and field is required, validate
      if (conditionMet && field.validations.when.params.then === 'required' && isValueEmpty) {
        fieldErrors.push(i18n.t('requiredField', { ns: 'common' }));
      }
    }

    // Handle direct required validation
    if (field.validations && field.validations.required) {
      const isValueMissing = !(name in allValues);
      if (isValueMissing) {
        fieldErrors.push(i18n.t('requiredField', { ns: 'common' }));
      }
    }

    if (field.validations && field.validations.min !== undefined && value < field.validations.min) {
      fieldErrors.push(i18n.t('minimumValue', { ns: 'common', min: field.validations.min }));
    }
    if (field.validations && field.validations.max !== undefined && value > field.validations.max) {
      fieldErrors.push(i18n.t('maximumValue', { ns: 'common', max: field.validations.max }));
    }

    setErrors((prev) => ({ ...prev, [name]: fieldErrors }));
  };

  const findFieldByName = (schemaObj, name) => {
    const sections = (schemaObj && schemaObj.sections) || [];
    for (let section of sections) {
      for (let group of (section.groups || [])) {
        for (let field of (group.fields || [])) {
          if (field.name === name) return field;
          const foundChild = findFieldInChildren(field.children, name);
          if (foundChild) return foundChild;
        }
      }
    }
    return null;
  };

  const findFieldInChildren = (children, name) => {
    const childrenList = children || [];
    for (let child of childrenList) {
      if (child.name === name) return child;
      const nested = findFieldInChildren(child.children, name);
      if (nested) return nested;
    }
    return null;
  };

  const isFieldVisible = (field) => {
    const conditionalDeps = (field.display && field.display.conditionalDeps) || [];
    return conditionalDeps.every((dep) => {
      const target = formData[dep.field];
      switch (dep.when.operator) {
        case 'equals':
          return target === dep.when.value;
        case 'not_equals':
          return target !== dep.when.value;
        default:
          return true;
      }
    });
  };

  const getDefaultValue = (field) => {
    if (field.type === 'boolean') return false;
    if (field.display && field.display.type === 'dropdown') return field.display.options[0].value;
    return '';
  };

  const renderField = (field) => {
    const visible = (field.display && field.display.display_always) || isFieldVisible(field);
    if (!visible) return null;

    const value = formData[field.name];
    const fieldErrors = errors[field.name] || [];
    const errorStyle = fieldErrors.length > 0 ? { borderColor: '#e24c4b' } : {};

    const wrapper = (inputElement) => (
      <div key={field.id} style={styles.fieldContainer}>
        {field.label && (
          <Tooltip target={`#label-${field.name}`} content={field.name} showDelay={1000} hideDelay={100} position='mouse' />
        )}
        {field.label && (
          <label
            id={`label-${field.name}`}
            style={styles.label}
          >
            {field.label}
          </label>
        )}
        {inputElement}
        {fieldErrors.map((e, i) => (
          <div key={i} style={styles.errorText}>{e}</div>
        ))}
      </div>
    );

    const displayType = (field.display && field.display.type) || 'string';
    switch (displayType) {
      case 'checkbox':
        return wrapper(
          <div className="p-field-checkbox">
            <Checkbox
              inputId={field.name}
              checked={!!value}
              onChange={(e) => updateValue(field.name, e.checked)}
            />
          </div>
        );

      case 'radio':
        return wrapper(
          <div className="p-formgroup-inline">
            {(field.display.options || []).map((opt) => (
              <div key={opt.value} className="p-field-radiobutton p-mr-3">
                <RadioButton
                  inputId={`${field.name}_${opt.value}`}
                  name={field.name}
                  value={opt.value}
                  onChange={(e) => updateValue(field.name, e.value)}
                  checked={value === opt.value}
                />
                <label htmlFor={`${field.name}_${opt.value}`}>{opt.label}</label>
              </div>
            ))}
          </div>
        );

      case 'dropdown':
        // Transform options to include value in parentheses
        const dropdownOptions = (field.display.options || []).map(opt => ({
          ...opt,
          label: opt.value !== opt.label ? `${opt.label} (${opt.value})` : opt.label
        }));

        return wrapper(
          <Dropdown
            value={value}
            options={dropdownOptions}
            optionLabel="label"
            optionValue="value"
            onChange={(e) => updateValue(field.name, e.value)}
            placeholder={i18n.t('select', { ns: 'common' })}
            style={errorStyle}
          />
        );

      case 'number':
        return wrapper(
          <InputNumber
            value={value}
            onValueChange={(e) => updateValue(field.name, e.value)}
            useGrouping={false}
            style={errorStyle}
          />
        );

      case 'string':
      default:
        return wrapper(
          <InputText
            value={value}
            onChange={(e) => updateValue(field.name, e.target.value)}
            style={errorStyle}
          />
        );
    }
  };

  const renderFields = (fields) => {
    return fields.flatMap((field) => {
      const main = renderField(field);
      const children = (field.children && field.children.length) ? renderFields(field.children) : [];
      return [main, ...children];
    });
  };

  const handleSubmit = () => {
    const traverseAndValidate = (fields) => {
      fields.forEach((field) => {
        const value = formData[field.name];
        validateField(field.name, value, formData);
        if (field.children && field.children.length) {
          traverseAndValidate(field.children);
        }
      });
    };

    const sectionsList = (schema && schema.sections) || [];
    sectionsList.forEach((section) => {
      const groups = section.groups || [];
      groups.forEach((group) => {
        traverseAndValidate(group.fields || []);
      });

    });

    const hasErrors = Object.values(errors).some((e) => e && e.length > 0);

    if (!hasErrors) {
      console.log('✅ Lomake tallennettu:', formData);
    } else {
      console.warn('❌ Lomakkeessa on virheitä');
    }
  };

  if (loading) {
    return <div>{i18n.t('loading', { ns: 'common' })}</div>;
  }

  if (error) {
    return <div style={{ color: '#e24c4b' }}>{error}</div>;
  }

  if (!schema) {
    return null;
  }

  const sectionsList = (schema && schema.sections) || [];

  return (
    <div className="oe-dynamic-form">
      {sectionsList.map((section) => (
        <div key={section.id}>
          {(section.groups || []).map((group) => (
            <Panel
              key={group.id}
              header={
                <div style={styles.groupHeader}>
                  {group.icon && <Image src={group.icon} alt="icon" width="20" height="20" style={styles.icon} />}
                  <span>{group.label}</span>
                </div>
              }
              toggleable
              collapsed={false}
              className="p-mb-3"
              style={{ marginBottom: '1rem' }}
            >
              {renderFields(group.fields || [])}
            </Panel>
          ))}
          <Divider />
        </div>
      ))}
    </div>
  );
};

export default OeDynamicForm;
